import React from 'react'
import { useDispatch } from 'react-redux'
import {
    TrendingUp,
    TrendingDown,
    Plus,
    ChevronRight,
    LayoutGrid,
    Wallet,
    ArrowUpRight,
    ArrowDownRight
} from 'lucide-react'
import { getColors } from '../../../theme/colors'
import { dateUtils } from '../../../api'
import { setActiveCategory, setActiveType } from '../../../store/slices/cashDayBookSlice'

const CategoryOverview = ({ categoryBreakdown, onAddEntry, loading }) => {
    const colors = getColors()
    const dispatch = useDispatch()

    const handleQuickAdd = (category, type) => {
        dispatch(setActiveCategory(category))
        dispatch(setActiveType(type))
        onAddEntry()
    }

    if (loading) {
        return (
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8'>
                {[1, 2, 3].map(i => (
                    <div
                        key={i}
                        className={`${colors.cardBg} ${colors.cardBgOpacity} border ${colors.cardBorder} rounded-xl p-6 animate-pulse`}
                    >
                        <div className='h-6 bg-gray-600 rounded w-1/2 mb-4'></div>
                        <div className='h-10 bg-gray-600 rounded w-full mb-4'></div>
                        <div className='flex gap-2'>
                            <div className='h-8 bg-gray-600 rounded flex-1'></div>
                            <div className='h-8 bg-gray-600 rounded flex-1'></div>
                        </div>
                    </div>
                ))}
            </div>
        )
    }

    if (!categoryBreakdown || categoryBreakdown.length === 0) {
        return (
            <div className={`${colors.cardBg} ${colors.cardBgOpacity} border ${colors.cardBorder} rounded-xl p-8 mb-8 text-center`}>
                <LayoutGrid className={`${colors.textMuted} mx-auto mb-4`} size={48} />
                <h3 className={`${colors.textPrimary} text-xl font-semibold mb-2`}>No Categories Yet</h3>
                <p className={`${colors.textSecondary} mb-6`}>Start by adding a transaction with a new category.</p>
                <button
                    onClick={() => onAddEntry()}
                    className={`bg-gradient-to-r ${colors.btnPrimaryFrom} ${colors.btnPrimaryTo} ${colors.textPrimary} px-6 py-2 rounded-lg hover:shadow-lg transition-all duration-300 flex items-center gap-2 mx-auto`}
                >
                    <Plus size={20} />
                    Add First Entry
                </button>
            </div>
        )
    }

    return (
        <div className='mb-8'>
            <div className='flex items-center justify-between mb-8'>
                <div>
                    <h3 className={`text-2xl font-bold ${colors.textPrimary} flex items-center gap-3`}>
                        <div className={`p-2 rounded-lg bg-gradient-to-br ${colors.btnPrimaryFrom} ${colors.btnPrimaryTo}`}>
                            <Wallet className="text-white" size={24} />
                        </div>
                        Financial Categories
                    </h3>
                    <p className={`${colors.textMuted} mt-1`}>
                        Manage and track expenses by specific categories
                    </p>
                </div>
                <div className={`${colors.cardBg} ${colors.cardBgOpacity} px-4 py-2 rounded-full border ${colors.cardBorder} text-sm font-medium ${colors.textSecondary}`}>
                    Total Categories: {categoryBreakdown.length}
                </div>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
                {categoryBreakdown.map((category, index) => (
                    <div
                        key={index}
                        className={`${colors.cardBg} ${colors.cardBgOpacity} border ${colors.cardBorder} hover:border-emerald-500/50 rounded-3xl overflow-hidden transition-all duration-500 hover:shadow-[0_20px_50px_rgba(0,0,0,0.3)] group relative`}
                    >
                        {/* Status Decoration */}
                        <div className={`absolute top-0 right-0 w-32 h-32 -mr-16 -mt-16 rounded-full blur-3xl opacity-20 transition-all duration-500 group-hover:opacity-40 ${category.netBalance >= 0 ? 'bg-emerald-500' : 'bg-red-500'
                            }`} />

                        <div className='p-8 relative'>
                            <div className='flex justify-between items-start mb-6'>
                                <div className='flex items-center gap-4'>
                                    <div className={`p-3 rounded-2xl ${category.netBalance >= 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
                                        }`}>
                                        <LayoutGrid size={24} />
                                    </div>
                                    <div>
                                        <h4 className={`text-2xl font-bold ${colors.textPrimary} group-hover:text-emerald-400 transition-colors duration-300`}>
                                            {category._id}
                                        </h4>
                                        <div className='flex items-center gap-2 mt-1'>
                                            <span className={`w-2 h-2 rounded-full ${category.netBalance >= 0 ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
                                            <span className={`${colors.textMuted} text-xs uppercase tracking-widest font-semibold`}>
                                                {category.totalCount} Transactions
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className='space-y-6'>
                                <div>
                                    <div className={`${colors.textMuted} text-xs uppercase tracking-[0.2em] font-bold mb-2`}>Net Position</div>
                                    <div className={`text-4xl font-black tracking-tight ${category.netBalance >= 0 ? 'text-emerald-400' : 'text-red-400'
                                        }`}>
                                        {dateUtils.formatCurrency(Math.abs(category.netBalance))}
                                        <span className='text-xs ml-2 font-bold opacity-60 uppercase'>
                                            {category.netBalance >= 0 ? 'Surplus' : 'Deficit'}
                                        </span>
                                    </div>
                                </div>

                                <div className='grid grid-cols-2 gap-4 p-4 rounded-2xl bg-black/20 border border-white/5'>
                                    <div>
                                        <div className='text-[10px] text-gray-500 uppercase font-black mb-1'>Income</div>
                                        <div className="text-emerald-400 font-bold flex items-center gap-1">
                                            <ArrowUpRight size={14} />
                                            {dateUtils.formatCurrency(category.totalIncome)}
                                        </div>
                                    </div>
                                    <div className='border-l border-white/10 pl-4'>
                                        <div className='text-[10px] text-gray-500 uppercase font-black mb-1'>Expense</div>
                                        <div className="text-red-400 font-bold flex items-center gap-1">
                                            <ArrowDownRight size={14} />
                                            {dateUtils.formatCurrency(category.totalExpense)}
                                        </div>
                                    </div>
                                </div>

                                <div className='flex gap-3 pt-2'>
                                    <button
                                        onClick={() => handleQuickAdd(category._id, 'Income')}
                                        className='flex-1 group/btn relative flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-500/10 hover:bg-emerald-500 text-emerald-400 hover:text-white font-bold transition-all duration-300 overflow-hidden'
                                    >
                                        <Plus size={18} className='group-hover/btn:scale-125 transition-transform' />
                                        Income
                                    </button>
                                    <button
                                        onClick={() => handleQuickAdd(category._id, 'Expense')}
                                        className='flex-1 group/btn relative flex items-center justify-center gap-2 py-3 rounded-xl bg-red-500/10 hover:bg-red-500 text-red-400 hover:text-white font-bold transition-all duration-300 overflow-hidden'
                                    >
                                        <Plus size={18} className='group-hover/btn:scale-125 transition-transform' />
                                        Expense
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default CategoryOverview
